import { useEffect, useState } from 'react';

import { fetchBulkSearch } from './bulkSearch.queries';
import { isBulkSearchInProgress } from './bulkSearch.types';
import {
    persistCurrentBulkSearch,
    readStoredBulkSearch,
    subscribeBulkSearchUpdates,
} from './bulkSearchSession';

const POLL_MS = 1500;

export function useBulkSearchLoading(bulkSearchId?: string): boolean {
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (!bulkSearchId) {
            setLoading(false);
            return undefined;
        }

        let cancelled = false;
        let intervalId: ReturnType<typeof setInterval> | undefined;

        const stopPolling = () => {
            if (intervalId !== undefined) {
                clearInterval(intervalId);
                intervalId = undefined;
            }
        };

        const refresh = async () => {
            try {
                const bulkSearch = await fetchBulkSearch(bulkSearchId);
                if (cancelled) {
                    return;
                }
                const inProgress = isBulkSearchInProgress(bulkSearch);
                setLoading(inProgress);
                persistCurrentBulkSearch(bulkSearch);
                if (!inProgress) {
                    stopPolling();
                }
            } catch {
                if (!cancelled) {
                    setLoading(false);
                    stopPolling();
                }
            }
        };

        const stored = readStoredBulkSearch();
        const sameStored = stored && String(stored.id) === String(bulkSearchId);
        setLoading(sameStored ? isBulkSearchInProgress(stored) : true);
        void refresh();
        intervalId = setInterval(() => {
            void refresh();
        }, POLL_MS);

        const unsubscribe = subscribeBulkSearchUpdates(() => {
            void refresh();
        });

        return () => {
            cancelled = true;
            stopPolling();
            unsubscribe();
        };
    }, [bulkSearchId]);

    return loading;
}
